import { z } from 'zod';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { v4 as uuidv4 } from 'uuid';

// GeoJSON coordinates can have 2 (lon, lat) or 3 (lon, lat, altitude) values
const coordinate2D = z.tuple([z.number(), z.number()]);
const coordinate3D = z.tuple([z.number(), z.number(), z.number()]);
const coordinate = z.union([coordinate2D, coordinate3D]);

// Define Point geometry schema
export const pointSchema = z.object({
  type: z.literal('Point'),
  coordinates: coordinate, // Expecting [longitude, latitude] or [longitude, latitude, altitude]
});

export const circleSchema = z.object({
  type: z.literal('Point'),
  coordinates: coordinate, // Expecting [longitude, latitude] or [longitude, latitude, altitude]
  radius: z.number(),
});

// Define LineString geometry schema
export const lineStringSchema = z.object({
  type: z.literal('LineString'),
  coordinates: z.array(coordinate), // Expecting an array of coordinates
});

// Define Polygon geometry schema
export const polygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(coordinate)), // Expecting an array of rings (arrays of coordinates)
});

// Union of the three possible geometries
export const geometrySchema = z.union([
  circleSchema,
  pointSchema,
  lineStringSchema,
  polygonSchema,
]);

export type Geometry = z.infer<typeof geometrySchema>;

export type CircleGeometry = z.infer<typeof circleSchema>;

export const getInitialGeometry = (
  shape: GeoJson | undefined,
  geometryType: string,
) => {
  if (shape?.geometry) {
    switch (geometryType) {
      case 'Point':
        if (shape.properties?.radius) {
          return circleSchema.parse({
            type: 'Point',
            coordinates: shape.geometry.coordinates,
            radius: shape.properties.radius,
          });
        }
        return pointSchema.parse({
          type: 'Point',
          coordinates: shape.geometry.coordinates,
        });
      case 'Circle':
        return circleSchema.parse({
          type: 'Point',
          coordinates: shape.geometry.coordinates,
          radius: shape.properties?.radius || 0,
        });
      case 'LineString':
        return lineStringSchema.parse({
          type: 'LineString',
          coordinates: shape.geometry.coordinates,
        });
      case 'Polygon':
        return polygonSchema.parse({
          type: 'Polygon',
          coordinates: shape.geometry.coordinates,
        });
    }
  } else {
    switch (geometryType) {
      case 'Point':
        return pointSchema.parse({ type: 'Point', coordinates: [0, 0] });
      case 'Circle':
        return circleSchema.parse({
          type: 'Point',
          coordinates: [0, 0],
          radius: 0,
        });
      case 'LineString':
        return lineStringSchema.parse({
          type: 'LineString',
          coordinates: [],
        });
      case 'Polygon':
        return polygonSchema.parse({ type: 'Polygon', coordinates: [[]] });
      default:
        throw new Error('Invalid geometry type');
    }
  }
};

export function createGeometryFromGeoJson(geojson: GeoJson) {
  // Check if the GeoJSON has a circle with a radius property
  if (geojson?.properties?.radius && geojson?.geometry?.type === 'Point') {
    const center = geojson.geometry.coordinates;
    const radius = geojson.properties.radius;

    return {
      type: 'Point',
      coordinates: center,
      radius,
    };
  }

  return geojson.geometry;
}

export function createGeoJsonFromGeometry(geometry: Geometry): GeoJson {
  if (geometry.type === 'Point' && 'radius' in geometry) {
    // This is a circle
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: geometry.coordinates,
        radius: geometry.radius,
      },
      properties: {
        id: uuidv4(),
        name: 'Shape drawn in MDM',
        radius: geometry.radius,
      },
    };
  }

  return {
    type: 'Feature',
    geometry,
    properties: {
      id: uuidv4(),
      name: 'Shape drawn in MDM',
    },
  };
}
